import React from "react";
import { useTranslation } from "react-i18next";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowCircleDown,
  faEnvelope,
  faPhone,
  faUsersSlash,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { ICustomer } from "redux/reducers/models/customer";
import "./mergeCustomers.scss";

type TCustomerDisplay = {
  full_name?: string;
  email?: string;
  phone?: string;
  member_code?: string;
  customer_type?: string;
  initials?: string;
  first_name?: string;
  last_name?: string;
  profile_image_source?: string | null;
  profile_image_source_url?: string | null;
};

export interface IMergeCustomersProps {
  open: boolean;
  currentCustomer: ICustomer | null;
  selectedCustomer: ICustomer | null;
  customerSearchQuery: string;
  customerSearchResults: Array<ICustomer>;
  customerSearching: boolean;
  /** When true, the current customer is kept and the selected customer is closed. */
  currentCustomerKeeps: boolean;
  onCancel: () => void;
  onContinue: () => void;
  onSearch: (query: string) => void;
  onSelect: (customer: ICustomer) => void;
  onRemoveSelected: () => void;
  onSwitchDirection: () => void;
}

function getCustomerInitials(customer: TCustomerDisplay): string {
  if (customer.initials?.trim()) {
    return customer.initials.trim().slice(0, 2).toUpperCase();
  }

  const first = customer.first_name?.charAt(0) ?? "";
  const last = customer.last_name?.charAt(0) ?? "";
  const initials = `${first}${last}`.toUpperCase();
  if (initials) {
    return initials;
  }

  return customer.full_name?.trim()?.charAt(0)?.toUpperCase() || "?";
}

function getCustomerSubtitle(customer: TCustomerDisplay): string {
  const type = customer.customer_type?.trim() || "";
  const memberCode = customer.member_code?.trim() || "";
  if (type && memberCode) {
    return `${type} • ${memberCode}`;
  }
  return type || memberCode || "—";
}

function CustomerDetailCard(props: {
  customer: TCustomerDisplay;
  selected?: boolean;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const { customer, selected, onRemove } = props;
  const imageSource = customer.profile_image_source_url || customer.profile_image_source;

  return (
    <div className={`merge-customers__card${selected ? " merge-customers__card--selected" : ""}`}>
      <div className="merge-customers__card-body">
        <div className="merge-customers__identity">
          {imageSource ? (
            <img className="merge-customers__avatar merge-customers__avatar--image" src={imageSource} alt="" />
          ) : (
            <div className="merge-customers__avatar" aria-hidden>
              {getCustomerInitials(customer)}
            </div>
          )}
          <div className="merge-customers__details">
            <span className="merge-customers__name">{customer.full_name || "—"}</span>
            <span className="merge-customers__subtitle">{getCustomerSubtitle(customer)}</span>
          </div>
        </div>

        <div className="merge-customers__card-divider" aria-hidden />

        <div className="merge-customers__contact">
          {customer.phone ? (
            <div className="merge-customers__contact-row">
              <FontAwesomeIcon icon={faPhone} className="merge-customers__contact-icon" />
              <span>{customer.phone}</span>
            </div>
          ) : null}
          {customer.email ? (
            <div className="merge-customers__contact-row">
              <FontAwesomeIcon icon={faEnvelope} className="merge-customers__contact-icon" />
              <span>{customer.email}</span>
            </div>
          ) : null}
          {!customer.phone && !customer.email ? (
            <div className="merge-customers__contact-row">
              <span>—</span>
            </div>
          ) : null}
        </div>
      </div>

      {onRemove ? (
        <button
          type="button"
          className="merge-customers__remove-button"
          aria-label={t("Remove selected customer")}
          onClick={onRemove}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      ) : null}
    </div>
  );
}

function CustomerSearchSelect(props: {
  open: boolean;
  searchQuery: string;
  searching: boolean;
  searchResults: Array<ICustomer>;
  onSearch: (query: string) => void;
  onSelect: (customer: ICustomer) => void;
}) {
  const { t } = useTranslation();
  const { Option } = Select;

  return (
    <Select
      showSearch
      className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200"
      onSearch={props.onSearch}
      onChange={(id: number, customer: ICustomer) => props.onSelect(customer)}
      placeholder={t("Search by name, email, phone or member number...")}
      allowClear
      searchValue={props.searchQuery}
      showDropDownOnFocus={true}
      searching={props.searching}
      autoFocus={props.open}
      noData={
        <div className="ui-select-dropdown-list-item">
          <span>{t("No users found...")}</span>
          <FontAwesomeIcon style={{ marginLeft: "5px" }} icon={faUsersSlash} size="1x" />
        </div>
      }
    >
      {props.searchResults?.map((customer, index) => {
        return (
          <Option key={index} value={customer.id} name={customer.full_name} extraValues={customer}>
            <div className="flex justify-between">
              <div>
                <div className="text-semibold text-lg">{customer?.full_name}</div>
                <div className="text-sm text-gray-500">{customer.customer_type}</div>
                <div className="text-sm text-gray-500">{customer.email}</div>
                <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
              </div>

              <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
            </div>
          </Option>
        );
      })}
    </Select>
  );
}

export default function MergeCustomers(props: IMergeCustomersProps) {
  const { t } = useTranslation();
  const {
    open,
    currentCustomer,
    selectedCustomer,
    customerSearchQuery,
    customerSearchResults,
    customerSearching,
    currentCustomerKeeps,
    onCancel,
    onContinue,
    onSearch,
    onSelect,
    onRemoveSelected,
    onSwitchDirection,
  } = props;

  const hasSelectedCustomer = !!selectedCustomer;
  const currentName = currentCustomer?.full_name || "—";
  const selectedName = selectedCustomer?.full_name || "—";

  const helperText = currentCustomerKeeps
    ? hasSelectedCustomer
      ? t("{{selectedName}} will be closed and merged into {{currentName}}.", {
          selectedName,
          currentName,
        })
      : t("The customer you select will be closed and merged into {{currentName}}.", {
          currentName,
        })
    : hasSelectedCustomer
      ? t("{{currentName}} will be closed and merged into {{selectedName}}.", {
          currentName,
          selectedName,
        })
      : t("{{currentName}} will be closed and merged into the customer you select.", {
          currentName,
        });

  const selectedOrSearch = hasSelectedCustomer ? (
    <CustomerDetailCard customer={selectedCustomer} selected onRemove={onRemoveSelected} />
  ) : (
    <CustomerSearchSelect
      open={open}
      searchQuery={customerSearchQuery}
      searching={customerSearching}
      searchResults={customerSearchResults}
      onSearch={onSearch}
      onSelect={onSelect}
    />
  );

  const currentCard = currentCustomer ? <CustomerDetailCard customer={currentCustomer} /> : null;

  const closeContent = currentCustomerKeeps ? selectedOrSearch : currentCard;
  const keepContent = currentCustomerKeeps ? (
    currentCard
  ) : (
    <div className="merge-customers__search">
      {selectedOrSearch}
      <p className="merge-customers__helper">{helperText}</p>
    </div>
  );

  return (
    <Sheet
      title={t("Merge customer accounts")}
      supportText={t("Select the account to keep, or switch the direction if needed.")}
      open={open}
      size="modal"
      closable
      onCancel={onCancel}
      onOk={onContinue}
      okText={t("Continue")}
      okDisabled={!hasSelectedCustomer}
      height="flexible"
    >
      <div className="merge-customers">
        <div className="merge-customers__section">
          <p className="merge-customers__section-title">{t("To close:")}</p>
          {closeContent}
        </div>

        <div className="merge-customers__transition">
          <div className="merge-customers__transition-line" />
          <Button type="default" size="medium" onClick={onSwitchDirection}>
            <span className="merge-customers__switch-content">
              <FontAwesomeIcon icon={faArrowCircleDown} />
              {t("Switch")}
            </span>
          </Button>
          <div className="merge-customers__transition-line" />
        </div>

        <div className="merge-customers__section">
          <p className="merge-customers__section-title">{t("To keep:")}</p>
          {keepContent}
        </div>

        {currentCustomerKeeps ? <p className="merge-customers__helper merge-customers__helper--footer">{helperText}</p> : null}
      </div>
    </Sheet>
  );
}
